import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { Organization, UpdateOrganizationRequest, SdkRateLimitUsage } from '../types';
import toast from 'react-hot-toast';

interface DataDeletionPreview {
    records_to_delete: {
        payment_intents: number;
        payment_events: number;
        payment_states: number;
        revenue_records: number;
        webhook_events: number;
        risk_flags: number;
        detected_patterns: number;
        payment_features: number;
        reports: number;
        audit_logs: number;
    };
    total_records: number;
    usage_info: {
        current_usage: number;
        usage_limit: number;
        usage_percentage: number;
        note: string;
    };
    preserved_data: {
        account: string;
        team_members: string;
        api_keys: string;
        provider_configs: string;
        settings: string;
    };
    warnings: string[];
    last_deletion: string | null;
}

interface DeleteAllDataRequest {
    confirm: string;
    password: string;
}

interface DeleteAllDataResponse {
    deleted_counts: Record<string, number>;
    total_deleted: number;
    usage_preserved: {
        payment_count: number;
        message: string;
    };
    deleted_at: string;
}

// Get organization
export const useOrganization = () => {
    return useQuery({
        queryKey: ['organization'],
        queryFn: async () => {
            const { data } = await api.get<{ success: boolean; data: Organization }>('/organization');
            return data.data;
        },
    });
};

// Get SDK rate limit usage for all API keys
export const useSdkRateLimitUsage = () => {
    return useQuery({
        queryKey: ['sdk-rate-limit'],
        queryFn: async () => {
            const { data } = await api.get<{ success: boolean; data: SdkRateLimitUsage }>('/organization/sdk-rate-limit');
            return data.data;
        },
        refetchInterval: 60000, // Refresh every 60 seconds
        retry: false, // Don't retry on 404
    });
};

// Update organization
export const useUpdateOrganization = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (updateData: UpdateOrganizationRequest) => {
            const { data } = await api.put<{ success: boolean; data: Organization }>('/organization', updateData);
            return data.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['organization'] });
            toast.success('Organization updated successfully');
        },
        onError: () => {
            toast.error('Failed to update organization');
        },
    });
};

// Get data deletion preview
export const useDataDeletionPreview = () => {
    return useQuery({
        queryKey: ['data-deletion-preview'],
        queryFn: async () => {
            const { data } = await api.get<{ success: boolean; data: DataDeletionPreview }>('/organization/data-deletion/preview');
            return data.data;
        },
        enabled: false, // Only fetch when manually triggered
    });
};

// Delete all organization data
export const useDeleteAllData = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (deleteData: DeleteAllDataRequest) => {
            const { data } = await api.post<{ success: boolean; message: string; data: DeleteAllDataResponse }>(
                '/organization/data-deletion/delete-all',
                deleteData
            );
            return data;
        },
        onSuccess: (response) => {
            queryClient.invalidateQueries({ queryKey: ['organization'] });
            queryClient.invalidateQueries({ queryKey: ['payments'] });
            queryClient.invalidateQueries({ queryKey: ['risks'] });
            queryClient.invalidateQueries({ queryKey: ['reports'] });
            toast.success(response.message);
        },
    });
};
